import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Avatar } from "shared/types/avatar";
import { Gender } from "shared/types/common";

import {
  ACTION_READ,
  ACTION_UNREAD,
  ACTIVITY_TYPE_FAVORITE,
  ACTIVITY_TYPE_LIKE,
  ACTIVITY_TYPE_VISITOR,
  ACTIVITY_TYPE_WINK,
} from "shared/constants/activity";

type ActivityTypes =
  | typeof ACTIVITY_TYPE_LIKE
  | typeof ACTIVITY_TYPE_WINK
  | typeof ACTIVITY_TYPE_VISITOR
  | typeof ACTIVITY_TYPE_FAVORITE;

export type ActivityT = {
  uid: number;
  acted_user_id: number;
  age: string;
  created_at: number;
  first_name: string;
  is_online: boolean;
  location: string;
  status: typeof ACTION_UNREAD | typeof ACTION_READ;
  type: ActivityTypes;
  gender?: Gender;
  avatar?: Avatar;
};

type ActivityStateT = {
  items: ActivityT[];
  token: string;
  uploaded: boolean;
  send: boolean;
  blur_notification: boolean | null;
  error: {
    error: boolean;
    errorCode: string | null;
    message: string;
  };
};

export const initialState: ActivityStateT = {
  items: [],
  token: "",
  uploaded: false,
  send: false,
  error: {
    error: false,
    errorCode: null,
    message: "",
  },
  blur_notification: null,
};

const activitySlice = createSlice({
  name: "activity",
  initialState,
  reducers: {
    clearActivities: (state) => {
      return {
        ...state,
        initialState,
      };
    },
    setActivities: (state, { payload }: PayloadAction<ActivityT[]>) => {
      return {
        ...state,
        items: payload,
        uploaded: true,
        send: false,
      };
    },
    setBlurNotification: (state, { payload }: PayloadAction<boolean>) => {
      return {
        ...state,
        blur_notification: payload,
        uploaded: true,
        send: false,
      };
    },
    toggleSend: (state, { payload }: PayloadAction<boolean>) => {
      state.send = payload;
    },
    responseErrorActivity: (state, { payload }) => {
      return {
        ...state,
        send: false,
        error: {
          error: true,
          errorCode: payload.status,
          message: payload.statusText,
        },
      };
    },
    setReadActivities: (state) => {
      for (const [index, item] of state.items.entries()) {
        if (item.status === ACTION_UNREAD) {
          state.items[index].status = ACTION_READ;
        }
      }
      return state;
    },
    setReadActivity: (state, { payload }: PayloadAction<number>) => {
      for (const [index, item] of state.items.entries()) {
        if (item.uid === payload && item.status === ACTION_UNREAD) {
          state.items[index].status = ACTION_READ;
          break;
        }
      }
      return state;
    },
  },
});

export const {
  setActivities,
  clearActivities,
  toggleSend,
  setReadActivities,
  setReadActivity,
  setBlurNotification,
  responseErrorActivity,
} = activitySlice.actions;

export default activitySlice.reducer;
